import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetActionsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  useGetActionsRegister,
  type UseGetActionsRegisterArgs,
} from 'src/hooks/queries/action/useGetActionsRegister';
import type { ActionTableFields } from 'src/pages/actions/types';

import { actionRegisterUrl } from '@/utils/urls';

import { useGetActionSmartWidgetTableProps } from '../../../actions/config';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilterWithUnrated,
  tagAndDepartmentCategoryGetters,
} from '../dataSourceHelpers';

export default createDataSource<
  ActionTableFields,
  UseGetActionsRegisterArgs,
  GetActionsQuery
>({
  group: 'single',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Action],
  useWidgetQuery: useGetActionsRegister,
  useTablePropsHook: (data, options) =>
    useGetActionSmartWidgetTableProps(data?.action, options),
  entityNamePlural: 'action_other',
  entityNameSingular: 'action_one',
  fields: 'actions.fields',
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ tagTypeIds: tags }),
    departmentsFilter: (departments) => ({ departmentTypeIds: departments }),
    dateFilter: (dateRange, precision) => ({
      dateRange: { field: 'DateRaised', ...toIsoBounds(dateRange, precision) },
    }),
    dateClickthroughFilter: dashboardDateRangeClickthroughFilter('DateRaised'),
    ownershipFilter: (myItemsFilters) => ({ ownership: myItemsFilters }),
  },
  clickThroughUrl: (filters) => actionRegisterUrl(filters),
  categoryGetters: [
    ...tagAndDepartmentCategoryGetters<ActionTableFields>(),
    {
      id: 'status',
      fieldId: 'Status',
      name: () => i18n.t('actions.columns.status'),
      categoryGetter: (item) => ({
        key: item.StatusLabelled.toLowerCase(),
        label: item.StatusLabelled,
      }),
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('StatusLabelled'),
      ratingColourKey: 'action_status',
    },
    {
      id: 'priority',
      fieldId: 'Priority',
      name: () => i18n.t('actions.columns.priority'),
      categoryGetter: (item) => ({
        sortKey: `${item.Priority}`,
        key: item.Priority,
        label: item.PriorityLabelled,
      }),
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('PriorityLabelled'),
      ratingColourKey: 'priority',
    },
    {
      id: 'owner',
      fieldId: 'Owners',
      name: () => i18n.t('actions.columns.owner'),
      categoryGetter: (item) =>
        item.allOwners.map((owner) => ({ key: owner.id, label: owner.label })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'contributor',
      fieldId: 'Contributors',
      name: () => i18n.t('actions.columns.contributor'),
      categoryGetter: (item) =>
        item.allContributors.map((contributor) => ({
          key: contributor.id,
          label: contributor.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allContributors',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'dateRaised',
      fieldId: 'DateRaised',
      name: () => `${i18n.t('actions.columns.date_raised')} Date`,
      categoryGetter: (data) => new Date(data.DateRaised),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'DateRaised',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('DateRaised'),
    },
    {
      id: 'targetCloseDate',
      fieldId: 'DateDue',
      name: () => `${i18n.t('actions.columns.due_date')} Date`,
      categoryGetter: (data) => new Date(data.DateDue),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: { field: 'DateDue', ...toIsoBounds(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('DateDue'),
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) => new Date(data.CreatedAtTimestamp),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'CreatedAtTimestamp',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
    },
  ],
});
